import { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';

const navLinks = [
  { to: '/land', label: 'The Land' },
  { to: '/vision', label: 'Vision' },
  { to: '/lots', label: 'Lots' },
  { to: '/calculator', label: 'Calculator' },
  { to: '/reserve', label: 'Reserve' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || open
          ? 'bg-cream/95 backdrop-blur-md shadow-[0_8px_30px_-12px_rgba(31,47,36,0.15)] border-b border-forest-700/10'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className={`flex items-center justify-between transition-all duration-500 ${scrolled ? 'h-20' : 'h-28'}`}>
          {/* Logo */}
          <Link to="/" className="flex items-center group">
            <img
              src="/image-1779155121233.png"
              alt="Costa Nativa"
              className={`w-auto transition-all duration-500 group-hover:scale-105 ${scrolled ? 'h-14' : 'h-20'}`}
            />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-9">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                className={({ isActive }) =>
                  `relative text-sm tracking-wide font-medium transition-colors py-2 ${
                    isActive
                      ? 'text-papaya-600'
                      : scrolled
                      ? 'text-forest-800 hover:text-papaya-600'
                      : 'text-forest-800 hover:text-papaya-600'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {link.label}
                    <span
                      className={`absolute left-0 right-0 -bottom-0.5 h-px bg-gradient-to-r from-papaya-500 to-lagoon-500 origin-left transition-transform duration-300 ${
                        isActive ? 'scale-x-100' : 'scale-x-0'
                      }`}
                    />
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          {/* CTA */}
          <Link
            to="/reserve"
            className="hidden lg:inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-papaya-500 to-hibiscus-500 text-cream text-sm font-medium tracking-wide hover:shadow-glow-papaya hover:-translate-y-0.5 transition-all"
          >
            Reserve a Lot
            <span aria-hidden>→</span>
          </Link>

          {/* Mobile button */}
          <button
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden w-10 h-10 flex items-center justify-center text-forest-900"
            aria-label="Toggle menu"
            aria-expanded={open}
          >
            <div className="space-y-1.5">
              <span
                className={`block w-6 h-0.5 bg-forest-900 transition-transform ${
                  open ? 'rotate-45 translate-y-2' : ''
                }`}
              />
              <span
                className={`block w-6 h-0.5 bg-forest-900 transition-opacity ${
                  open ? 'opacity-0' : ''
                }`}
              />
              <span
                className={`block w-6 h-0.5 bg-forest-900 transition-transform ${
                  open ? '-rotate-45 -translate-y-2' : ''
                }`}
              />
            </div>
          </button>
        </div>

        {/* Mobile menu */}
        {open && (
          <nav className="lg:hidden pb-6 pt-2 border-t border-forest-900/10">
            <ul className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <li key={link.to}>
                  <NavLink
                    to={link.to}
                    className={({ isActive }) =>
                      `block py-3 text-base font-display tracking-wide ${
                        isActive ? 'text-papaya-600' : 'text-forest-900'
                      }`
                    }
                  >
                    {link.label}
                  </NavLink>
                </li>
              ))}
              <li className="pt-3">
                <Link
                  to="/reserve"
                  className="block w-full text-center py-3 rounded-full bg-gradient-to-r from-papaya-500 to-hibiscus-500 text-cream text-sm font-medium tracking-wide"
                >
                  Reserve a Lot →
                </Link>
              </li>
            </ul>
          </nav>
        )}
      </div>
    </header>
  );
}
